<script setup lang="ts">
import PdsBadge from '@/components/pds/PdsBadge.vue';
import PdsButton from '@/components/pds/PdsButton.vue';
import { computed, ref } from 'vue';
import { useDisplayPrefs } from '../composables/useDisplayPrefs';
import { formatDate, formatMoney, formatTime } from '../format';
import type { PdsOrder } from '../types';
import FoldTransition from './FoldTransition.vue';
import ItemRow from './ItemRow.vue';

// `unfolded` rend la carte pilotée par le parent (colonne en accordéon) ; sans lui, elle se déplie seule.
const props = defineProps<{
  order: PdsOrder;
  late: boolean;
  unfolded?: boolean;
}>();
const emit = defineEmits<{
  open: [];
  fold: [];
  toggleItem: [itemId: string];
  markReady: [];
  handOver: [];
}>();

const prefs = useDisplayPrefs();

const preparing = () => props.order.stage === 'preparing';

// Toucher la carte la déplie sur place ; un bouton ou une case dans la carte garde son action.
const ownUnfolded = ref(false);
const isUnfolded = computed(() => props.unfolded ?? ownUnfolded.value);
const toggleFold = () => {
  if (props.unfolded === undefined) ownUnfolded.value = !ownUnfolded.value;
  else emit('fold');
};
// Le chemin est lu tel qu'il était au clic : décocher retire l'icône ✓ avant que l'événement remonte ici, et
// `target.closest` ne retrouverait plus le bouton.
const onCardClick = (event: Event) => {
  const onControl = event
    .composedPath()
    .some((el) => el instanceof HTMLElement && el.matches('button, a'));
  if (!onControl) toggleFold();
};
const showItems = () => prefs.expanded || isUnfolded.value;
</script>

<template>
  <article
    class="bg-carte flex cursor-pointer flex-col rounded-[24px]"
    :class="
      prefs.compact
        ? 'gap-2 p-4 [--fold-gap:8px]'
        : 'gap-[10px] p-6 [--fold-gap:10px]'
    "
    tabindex="0"
    :aria-expanded="isUnfolded"
    @click="onCardClick"
    @keydown.enter.self="toggleFold"
  >
    <header class="flex h-7 items-center gap-3">
      <span class="shrink-0 text-[18px] leading-[1.2] font-bold">
        #{{ order.orderNumber ?? order.number }}
      </span>
      <span
        class="text-encre-douce min-w-0 flex-1 truncate text-[12px] leading-[1.45]"
      >
        {{ order.label }}
      </span>
      <PdsBadge v-if="late" tone="late">Retard</PdsBadge>
    </header>

    <p
      class="flex flex-wrap gap-x-[5px] text-[24px] leading-[28px] font-bold tracking-[-0.4px] whitespace-nowrap"
      :class="late ? 'text-alerte-fonce' : 'text-encre'"
    >
      <span>{{ formatDate(order.dueAt) }}</span>
      <span>· {{ formatTime(order.dueAt) }}</span>
    </p>

    <p
      v-if="prefs.itemCount || prefs.amount"
      class="text-encre-douce text-[12px] leading-[1.45]"
    >
      <template v-if="prefs.itemCount">
        {{ order.items.length }}
        produit{{ order.items.length > 1 ? 's' : '' }}
      </template>
      <template v-if="prefs.itemCount && prefs.amount"> · </template>
      <template v-if="prefs.amount">
        {{ formatMoney(order.totalPaidCents) }}
      </template>
    </p>

    <p
      v-if="prefs.customerNote && order.customerNote"
      class="bg-creux flex w-fit max-w-full items-center gap-2 rounded-[8px] px-[10px] py-[6px] text-[12px]"
    >
      <span
        class="text-encre-douce leading-[1.2] font-semibold tracking-[0.9px] uppercase"
      >
        Client
      </span>
      <span class="truncate leading-[1.45]">{{ order.customerNote }}</span>
    </p>

    <FoldTransition>
      <div v-if="showItems() && !preparing()" class="flex flex-col">
        <ItemRow
          v-for="item in order.items"
          :key="item.id"
          :item="item"
          :compact="prefs.compact"
        />
      </div>
    </FoldTransition>

    <FoldTransition>
      <div v-if="preparing() && showItems()" class="flex flex-col">
        <ItemRow
          v-for="item in order.items"
          :key="item.id"
          :item="item"
          selectable
          stable
          @toggle="emit('toggleItem', item.id)"
        />
      </div>
    </FoldTransition>

    <div v-if="order.stage === 'ready'" class="flex">
      <PdsButton variant="second" @click="emit('handOver')">
        Remettre au client
      </PdsButton>
    </div>
    <div v-else-if="preparing()" class="flex gap-[10px]">
      <PdsButton v-if="showItems()" @click="emit('markReady')">
        Marquer prête
      </PdsButton>
      <PdsButton variant="second" @click="emit('open')">Voir plus</PdsButton>
    </div>
    <div v-else class="flex">
      <PdsButton variant="second" @click="emit('open')">
        Voir la commande
      </PdsButton>
    </div>
  </article>
</template>
